import {
  Badge,
  Box,
  Button,
  Grid,
  Group,
  Modal,
  Stack,
  Switch,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAdjustmentsHorizontal,
  IconChevronRight,
  IconHistory,
  IconCopy,
  IconEye,
  IconEyeOff,
  IconLock,
  IconLockOpen,
  IconMessage2Question,
  IconX,
} from "@tabler/icons-react";
import cardDebitImg from "assets/images/pngs/debitCard.png";
import cardCreditImg from "assets/images/pngs/creditcards.png";
import debitCardDetailImg from "assets/images/pngs/debitCardDetail.png";
import creditCardDetailImg from "assets/images/pngs/creditCardDetail.png";
// Bộ icon menu thẻ tín dụng — SVG lấy trực tiếp từ Figma (đúng glyph + màu ô + màu icon).
import icLock from "assets/icons/card-detail/ic-lock.svg";
import icHistory from "assets/icons/card-detail/ic-history.svg";
import icLink from "assets/icons/card-detail/ic-link.svg";
import icStatement from "assets/icons/card-detail/ic-statement.svg";
import icInstallment from "assets/icons/card-detail/ic-installment.svg";
import icPayment from "assets/icons/card-detail/ic-payment.svg";
import icScope from "assets/icons/card-detail/ic-scope.svg";
import icLimit from "assets/icons/card-detail/ic-limit.svg";
import icOnline from "assets/icons/card-detail/ic-online.svg";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { beText } from "@/utils/beText";
import RoutesString from "pages/routesString";
import { SHA256 } from "crypto-js";
import { useForm } from "@mantine/form";
import OtpModal from "@/components/common/modal/otp-modal/OtpModal";
import {
  useGetCreditCardDetailQuery,
  useGetDebitCardDetailQuery,
  useInitGetSensitiveDebitCardInfoMutation,
  useInitGetSensitiveCreditCardInfoMutation,
  useInitLinkToBizMutation,
  useInitLinkToBizCreditCardMutation,
  useLinkToBizMutation,
  useLinkToBizCreditCardMutation,
} from "@/services/cardAPI";
import { useGenerateOTPMutation } from "@/services/authAPI";
import { PAGES_URL, RESULT_CODE } from "@/constants/enum";
import {
  CardApprovalProcessInfo,
  CardAuthObject,
  DebitCardItem,
  GetSensitiveDebitCardInfoResponse,
} from "@/dto/card/response/CardResponse";
import { get } from "lodash";
import CardDetailActionDrawer from "./CardDetailActionDrawer";
import AuthenticationMode from "./component/authenticationMode";
import CardPermissionErrorModal from "../components/CardPermissionErrorModal";
import { getOtpPhoneNumber } from "@/utils/otpPhone";

/** Mục hành động ở sidebar chi tiết thẻ. `img` (SVG credit) hoặc `icon` (Tabler, debit). */
type ActionItem = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
  img?: string;
  color?: string;
  bg?: string;
  label: string;
  type: string;
  visible: boolean;
};

const CardDetailWrapper = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cardId } = useParams();
  const location = useLocation();
  /** Card item từ màn danh sách truyền qua navigate state — chứa linkToBiz, customerName... */
  const navState = location.state as
    | {
        cardListItem?: DebitCardItem & { cardProduct?: string };
        cardKind?: string;
      }
    | null;
  const cardListItem = navState?.cardListItem;
  /**
   * Phân biệt loại thẻ để gọi đúng API chi tiết (debit vs credit).
   * Ưu tiên route (tách riêng /cards/credit-detail) để refresh/deep-link vẫn đúng,
   * fallback về cardKind trong navigate state.
   */
  const isCredit =
    location.pathname.includes(PAGES_URL.CARD_CREDIT_DETAIL) ||
    navState?.cardKind === "CREDIT";
  const numericCardId = Number(cardId);
  const isInvalidCardId = !cardId || Number.isNaN(numericCardId);
  const { data: debitDetailData } = useGetDebitCardDetailQuery(
    { cardId: numericCardId },
    {
      skip: isCredit || isInvalidCardId,
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: creditDetailData } = useGetCreditCardDetailQuery(
    { cardId: numericCardId },
    {
      skip: !isCredit || isInvalidCardId,
      refetchOnMountOrArgChange: true,
    },
  );
  const cardDetailData = isCredit ? creditDetailData : debitDetailData;
  const cardDetail = get(cardDetailData, "body");
  /**
   * Nhãn trạng thái thẻ (#56083): ưu tiên MÃ trạng thái -> khoá i18n, chỉ lùi về
   * `statusLabel` (backend trả tiếng Việt) qua `beText` khi gặp mã lạ.
   *
   * Tách ra khỏi JSX vì viết dạng ternary lồng nhau vi phạm `no-nested-ternary`
   * và làm cả nhánh develop không build được.
   */
  const CARD_STATUS_KEY: Record<string, string> = { L: "LOCKED", A: "ACTIVE" };

  /** Lỗi quyền/thao tác từ API chi tiết: resultCode khác SUCCESS sau khi đã có response. */
  const detailResultCode = get(cardDetailData, "body.resultCode");
  const hasDetailError =
      !!cardDetailData && detailResultCode !== RESULT_CODE.SUCCESS;
  const detailErrorMessage: string =
      get(cardDetailData, "body.resultDesc", "") || "";

  const [initGetSensitiveDebitCardInfo, { isLoading: isCheckingScope }] =
    useInitGetSensitiveDebitCardInfoMutation();
  // JVC-3543: thẻ tín dụng dùng cặp API sensitive riêng
  const [initGetSensitiveCreditCardInfo] =
    useInitGetSensitiveCreditCardInfoMutation();
  const [generateOTP, { isLoading: isGeneratingOtp }] =
    useGenerateOTPMutation();
  const [initLinkToBiz, { isLoading: isInitLinkingDebit }] =
    useInitLinkToBizMutation();
  const [linkToBiz, { isLoading: isConfirmingLinkDebit }] =
    useLinkToBizMutation();
  const [initLinkToBizCredit, { isLoading: isInitLinkingCredit }] =
    useInitLinkToBizCreditCardMutation();
  const [linkToBizCredit, { isLoading: isConfirmingLinkCredit }] =
    useLinkToBizCreditCardMutation();
  const isInitLinking = isInitLinkingDebit || isInitLinkingCredit;
  const isConfirmingLink = isConfirmingLinkDebit || isConfirmingLinkCredit;
  const [viewRefId, setViewRefId] = useState<string>("");
  const [viewOtpId, setViewOtpId] = useState<string>("");
  const [sensitiveInfo, setSensitiveInfo] =
    useState<GetSensitiveDebitCardInfoResponse | null>(null);
  const [showCvv, setShowCvv] = useState<boolean>(false);
  const [labelAction, setLabelAction] = useState<string>("");
  const [openedDrawer, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const [openedModal, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [openedAction, { open: openAction, close: closeAction }] =
    useDisclosure(false);
  const [countdown, setCountdown] = useState(59);
  /** Khởi tạo từ `linkToBiz` của card item màn danh sách truyền qua state. */
  const [linked, setLinked] = useState<boolean>(!!cardListItem?.linkToBiz);
  const [openedToggleWarn, { open: openToggleWarn, close: closeToggleWarn }] =
    useDisclosure(false);
  // JVC-3542: popup cảnh báo khi thẻ chưa liên kết Javis Biz mà thao tác quản lý thẻ
  const [
    openedNotLinkedWarn,
    { open: openNotLinkedWarn, close: closeNotLinkedWarn },
  ] = useDisclosure(false);
  /** Popup khi API chi tiết trả lỗi (vd không phải chủ thẻ — CC-REQ-003). */
  const [
    openedDetailError,
    { open: openDetailError, close: closeDetailError },
  ] = useDisclosure(false);

  /** Confirm modal khi user toggle Liên kết Javis Biz — click Đồng ý mới gọi API. */
  const [
    openedConfirmLink,
    { open: openConfirmLink, close: closeConfirmLink },
  ] = useDisclosure(false);

  /** Lưu refId + authObjects sau khi init thành công — dùng cho bước confirm OTP kế tiếp. */
  const [linkRefId, setLinkRefId] = useState("");
  const [linkAuthObjects, setLinkAuthObjects] = useState<CardAuthObject[]>([]);
  const [linkApprovalInfo, setLinkApprovalInfo] =
    useState<CardApprovalProcessInfo | null>(null);
  const [linkOtpId, setLinkOtpId] = useState("");
  const [openedLinkOtp, { open: openLinkOtp, close: closeLinkOtp }] =
    useDisclosure(false);
  const linkOtpForm = useForm({ initialValues: { otp: "" } });
  /** Lưu giá trị linkToBiz đang init để truyền vào API confirm + navigate state. */
  const [pendingLinkValue, setPendingLinkValue] = useState<boolean>(false);

  const isDebit = !isCredit;
  const cardImage = isDebit ? cardDebitImg : cardCreditImg;
  const cardModalImage = isDebit ? debitCardDetailImg : creditCardDetailImg;
  const fullCardNumber = cardDetail?.cardNumberMasked || "";

  const allowedActions = cardDetail?.allowedActions || [];
  const hasAllowedActions = allowedActions.length > 0;
  const isLocked = cardDetail?.status === "L";
  const allowAction = (key: string) =>
    !hasAllowedActions || allowedActions.includes(key as never);

  /**
   * Điều kiện thao tác ở màn chi tiết thẻ ghi nợ:
   * - Liên kết Javis Biz là ĐIỀU KIỆN CẦN cho mọi thao tác (kể cả xem lịch sử).
   * - Thẻ khóa + đã liên kết: chỉ cho Mở khóa (UNLOCK); hủy liên kết qua toggle.
   * - Thẻ active + đã liên kết: full quyền (Khóa, đổi hạn mức...).
   * - Chưa liên kết: không thao tác nào (kể cả thẻ đang Active).
   * (Thẻ tín dụng giữ nguyên hành vi cũ.)
   */
  const isActionEnabled = (type: string): boolean => {
    if (isCredit) return true;
    if (!linked) return false;
    if (isLocked) return type === "UNLOCK";
    return true;
  };

  const cardInfo = {
    holderName: cardListItem?.customerName || cardDetail?.cardName || "-",
    accountNumber: cardDetail?.accountNo || "-",
    availableLimit: cardDetail
      ? `${Number(cardDetail.availableBalance ?? 0).toLocaleString("vi-VN")} ${
          cardDetail.currency || "VND"
        }`
      : "-",
    productName: cardDetail?.cardProduct || cardListItem?.cardProduct || "-",
    company: "-",
    address: cardDetail?.customerAddress || "-",
    cardName: cardDetail?.cardName || "-",
    cardBusiness: cardDetail?.cardBusiness || "-",
  };

  /**
   * Thẻ tín dụng dùng bộ allowedActions riêng từ BE (LOCK_UNLOCK, STATEMENT,
   * INSTALLMENT...). Hiển thị theo đúng thứ tự nghiệp vụ, lọc theo allowedActions.
   */
  const creditActions: ActionItem[] = [
    {
      img: icLock,
      label: t("LOCK_UNLOCK_CARD"),
      type: "LOCK_UNLOCK",
      visible: allowAction("LOCK_UNLOCK"),
    },
    {
      img: icHistory,
      label: t("TRANSACTION_HISTORY"),
      type: "TRANSACTION_HISTORY",
      visible: allowAction("TRANSACTION_HISTORY"),
    },
    {
      img: icLink,
      label: t("LINK_CARD_WITH_JAVIS_BIZ"),
      type: "LINK_BIZ",
      visible: allowAction("LINK_BIZ"),
    },
    {
      img: icStatement,
      label: t("CARD_STATEMENT"),
      type: "STATEMENT",
      visible: allowAction("STATEMENT"),
    },
    {
      img: icInstallment,
      label: t("INSTALLMENT_REGISTRATION"),
      type: "INSTALLMENT",
      visible: allowAction("INSTALLMENT"),
    },
    {
      img: icPayment,
      label: t("PAY_CREDIT_CARD"),
      type: "PAYMENT",
      visible: allowAction("PAYMENT"),
    },
    {
      img: icScope,
      label: t("USAGE_SCOPE_SETTING"),
      type: "USAGE_SETTING",
      visible: allowAction("USAGE_SETTING"),
    },
    {
      img: icLimit,
      label: t("CARD_USAGE_LIMIT"),
      type: "USAGE_LIMIT",
      // Thiết kế JVC-3528: luôn hiển thị mục "Hạn mức sử dụng thẻ" (BE chưa trả trong allowedActions).
      visible: true,
    },
    {
      img: icOnline,
      label: t("ONLINE_PAYMENT_REGISTRATION"),
      type: "ONLINE_PAYMENT",
      visible: allowAction("ONLINE_PAYMENT"),
    },
  ];

  const debitActions: ActionItem[] = [
    {
      icon: IconLock,
      color: "#2A81D0",
      bg: "#EEF5FF",
      label: t("LOCK_CARD"),
      type: "LOCK",
      visible: allowAction("LOCK") && !isLocked,
    },
    {
      icon: IconLockOpen,
      color: "#2A81D0",
      bg: "#EEF5FF",
      label: t("UNLOCK_CARD"),
      type: "UNLOCK",
      visible: allowAction("UNLOCK") && isLocked,
    },
    {
      icon: IconHistory,
      color: "#2A81D0",
      bg: "#EEF5FF",
      label: t("TRANSACTION_HISTORY"),
      type: "TRANSACTION_HISTORY",
      visible: true,
    },
    {
      icon: IconAdjustmentsHorizontal,
      color: "#2A81D0",
      bg: "#EEF5FF",
      label: t("SET_LIMIT"),
      type: "LIMIT",
      visible: allowAction("CHANGE_LIMIT"),
    },
  ];

  const allActions = isCredit ? creditActions : debitActions;
  /** Navigate sang page lịch sử giao dịch — dùng cho cả Box action + Button rời. */
  const handleViewTransactionHistory = () => {
    const path = cardId
      ? `${RoutesString.CardTransactionHistory}/${cardId}`
      : RoutesString.CardTransactionHistory;
    navigate(path, {
      state: { cardListItem, cardKind: isCredit ? "CREDIT" : undefined },
    });
  };
  const actions = allActions.filter((a) => a.visible);
  // JVC-3543: thẻ tín dụng luôn có nút "Xem chi tiết thẻ" (allowedActions từ ewallet
  // không chứa VIEW_SENSITIVE cho credit nhưng thiết kế yêu cầu có — Blocker).
  const canViewSensitive = allowAction("VIEW_SENSITIVE") || isCredit;

  useEffect(() => {
    if (!openedModal) return undefined;
    if (countdown <= 0) {
      closeModal();
      return undefined;
    }
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [openedModal, countdown, closeModal]);

  useEffect(() => {
    if (openedModal) {
      setCountdown(59);
    } else {
      setSensitiveInfo(null);
      setShowCvv(false);
    }
  }, [openedModal]);

  /** Khi API chi tiết trả lỗi → mở popup thông báo không có quyền thao tác. */
  useEffect(() => {
    if (hasDetailError) openDetailError();
  }, [hasDetailError]);

  /**
   * Đồng bộ trạng thái liên kết từ API chi tiết (nếu BE trả về `linkToBiz`),
   * để refresh/deep-link không bị mất trạng thái (state navigate đã mất).
   */
  useEffect(() => {
    if (typeof cardDetail?.linkToBiz === "boolean") {
      setLinked(cardDetail.linkToBiz);
    }
  }, [cardDetail?.linkToBiz]);

  /** Đóng popup lỗi → quay lại màn danh sách thẻ (không cho xem chi tiết). */
  const handleCloseDetailError = () => {
    closeDetailError();
    navigate(-1);
  };

  /** Sang màn Quản lý yêu cầu từ popup lỗi. */
  const handleGoManageRequest = () => {
    closeDetailError();
    navigate(RoutesString.RequirementsManagementPage);
  };

  /** Các action thẻ tín dụng chưa có màn hình riêng → báo đang phát triển. */
  const CREDIT_TODO_ACTIONS = ["INSTALLMENT", "USAGE_SETTING", "USAGE_LIMIT"];

  const handleOperation = (type: string) => {
    // JVC-3542: thẻ chưa liên kết Javis Biz → chặn các chức năng quản lý thẻ,
    // hiện popup hướng dẫn liên kết (thiết kế Figma) thay vì đi tiếp rồi lỗi.
    const requireLinkActions = [
      "LOCK",
      "UNLOCK",
      "LOCK_UNLOCK",
      "LIMIT",
      "LIMIT_SETTING",
      "ONLINE_PAYMENT",
      "PAYMENT",
      "STATEMENT",
      "INSTALLMENT",
      "USAGE_SETTING",
    ];
    if (!linked && requireLinkActions.includes(type)) {
      openNotLinkedWarn();
      return;
    }
    if (type === "LIMIT" || type === "LIMIT_SETTING") {
      const path = cardId
        ? `${RoutesString.CardLimitSetting}/${cardId}`
        : RoutesString.CardLimitSetting;
      navigate(path);
      return;
    }
    if (type === "UNLOCK") {
      // Mở khoá thẻ chuyển sang page riêng thay vì drawer
      const path = cardId
        ? `${RoutesString.CardUnlock}/${cardId}`
        : RoutesString.CardUnlock;
      navigate(path, { state: { cardListItem } });
      return;
    }
    if (type === "LOCK") {
      // Khoá thẻ chuyển sang page riêng (cùng pattern step-based với UNLOCK)
      const path = cardId
        ? `${RoutesString.CardLock}/${cardId}`
        : RoutesString.CardLock;
      navigate(path, { state: { cardListItem } });
      return;
    }
    if (type === "LOCK_UNLOCK") {
      // Thẻ tín dụng: 1 action gộp, điều hướng theo trạng thái khoá hiện tại.
      // Truyền cardKind để luồng khoá/mở gọi đúng API credit.
      const base = isLocked ? RoutesString.CardUnlock : RoutesString.CardLock;
      navigate(cardId ? `${base}/${cardId}` : base, {
        state: { cardListItem, cardKind: "CREDIT" },
      });
      return;
    }
    if (type === "LINK_BIZ") {
      if (isLocked) {
        openToggleWarn();
        return;
      }
      openConfirmLink();
      return;
    }
    if (type === "VIEW_SENSITIVE" || type === "TRANSACTION_HISTORY") {
      handleViewTransactionHistory();
      return;
    }
    if (type === "STATEMENT") {
      const path = cardId
        ? `${RoutesString.CardStatement}/${cardId}`
        : RoutesString.CardStatement;
      navigate(path, {
        state: { cardListItem, cardKind: isCredit ? "CREDIT" : undefined },
      });
      return;
    }
    if (type === "ONLINE_PAYMENT") {
      const path = cardId
        ? `${RoutesString.CardOnlinePayment}/${cardId}`
        : RoutesString.CardOnlinePayment;
      // Mặc định đăng ký (bật) thanh toán trực tuyến.
      navigate(path, {
        state: { cardListItem, cardKind: "CREDIT", isOnlinePayment: true },
      });
      return;
    }
    if (type === "PAYMENT") {
      const path = cardId
        ? `${RoutesString.CardPayment}/${cardId}`
        : RoutesString.CardPayment;
      navigate(path, { state: { cardListItem, cardKind: "CREDIT" } });
      return;
    }
    if (CREDIT_TODO_ACTIONS.includes(type)) {
      showNotification({
        title: t("NOTIFICATION"),
        message: t("FEATURE_IN_DEVELOPMENT"),
        color: "blue",
      });
      return;
    }
    setLabelAction(type);
    openAction();
  };

  const handleViewCardDetail = async () => {
    if (!cardId || Number.isNaN(numericCardId)) {
      showNotification({
        title: t("ERROR"),
        message: t("MISSING_CARD_INFO"),
        color: "red",
      });
      return;
    }
    try {
      // JVC-3543: thẻ tín dụng gọi init sensitive credit, thẻ ghi nợ giữ API cũ
      const initRes = await (isCredit
        ? initGetSensitiveCreditCardInfo({ cardId: numericCardId })
        : initGetSensitiveDebitCardInfo({ cardId: numericCardId })
      ).unwrap();
      if (initRes.body?.resultCode !== RESULT_CODE.SUCCESS) {
        showNotification({
          title: t("ERROR"),
          message:
            initRes.body?.resultDesc || t("INITIATE_VIEW_CARD_FAILED"),
          color: "red",
        });
        return;
      }
      const refId = initRes.body?.refId || "";
      const otpRes = await generateOTP({
        objectId: refId || String(numericCardId),
        objectType: "VIEW_CARD",
        otpType: "SMS_OTP",
        phoneNumber: "0987978978",
      }).unwrap();
      if (otpRes.body?.resultCode !== RESULT_CODE.SUCCESS) {
        showNotification({
          title: t("ERROR"),
          message: otpRes.body?.resultDesc || t("GET_OTP_FAILED"),
          color: "red",
        });
        return;
      }
      setViewRefId(refId);
      setViewOtpId(otpRes.body?.otpId || "");
      setSensitiveInfo(null);
      openDrawer();
    } catch (error) {
      showNotification({
        title: t("ERROR"),
        message: t("CONNECTION_ERROR"),
        color: "red",
      });
    }
  };

  const handleSensitiveInfoSuccess = (
    info: GetSensitiveDebitCardInfoResponse,
  ) => {
    setSensitiveInfo(info);
  };

  /**
   * Click toggle Liên kết Javis Biz — luôn cho phép (cả khi thẻ khóa):
   * - Khóa + chưa liên kết: gạt ON để liên kết trước, sau đó mới mở khóa được.
   * - Khóa + đã liên kết: gạt OFF để hủy liên kết.
   * Chỉ khi user click "Đồng ý" trong confirm modal mới gọi API.
   */
  const handleToggleLink = () => {
    if (!cardId || Number.isNaN(numericCardId)) {
      showNotification({
        title: t("ERROR"),
        message: t("MISSING_CARD_INFO"),
        color: "red",
      });
      return;
    }
    openConfirmLink();
  };

  /**
   * Đồng ý ở confirm modal → init → generateOTP → mở OTP modal.
   * Confirm OTP sẽ gọi `linkToBiz` ở `handleConfirmLinkOtp`.
   */
  const handleConfirmLink = async () => {
    if (!cardId || Number.isNaN(numericCardId)) return;
    const nextLinked = !linked;
    try {
      const res = await (isCredit ? initLinkToBizCredit : initLinkToBiz)({
        cardId: numericCardId,
        linkToBiz: nextLinked,
      }).unwrap();
      if (res.body?.resultCode !== RESULT_CODE.SUCCESS) {
        showNotification({
          title: t("ERROR"),
          message:
            res.body?.resultDesc || t("INITIATE_JAVIS_BIZ_LINK_FAILED"),
          color: "red",
        });
        return;
      }
      const refId = res.body?.refId || "";
      const authObjects = res.body?.authObjects || [];
      setLinkRefId(refId);
      setLinkAuthObjects(authObjects);
      setLinkApprovalInfo(res.body?.approvalProcessInfo || null);
      setPendingLinkValue(nextLinked);

      // Step 2: generateOTP
      const otpRes = await generateOTP({
        objectId: refId || String(numericCardId),
        objectType: "tranfer",
        otpType: "SMS_OTP",
        phoneNumber: "0987978978",
      }).unwrap();
      if (otpRes.body?.resultCode !== RESULT_CODE.SUCCESS) {
        showNotification({
          title: t("ERROR"),
          message: otpRes.body?.resultDesc || t("GET_OTP_FAILED"),
          color: "red",
        });
        return;
      }
      setLinkOtpId(otpRes.body?.otpId || "");
      linkOtpForm.reset();
      closeConfirmLink();
      openLinkOtp();
    } catch (err) {
      showNotification({
        title: t("ERROR"),
        message: t("CONNECTION_ERROR"),
        color: "red",
      });
    }
  };

  /** Gửi lại OTP trong modal liên kết Javis Biz — throw khi fail để modal giữ trạng thái hết hạn cho bấm lại. */
  const handleResendLinkOtp = async () => {
    const otpRes = await generateOTP({
      objectId: linkRefId || String(numericCardId),
      objectType: "tranfer",
      otpType: "SMS_OTP",
      phoneNumber: getOtpPhoneNumber(),
    }).unwrap();
    if (otpRes.body?.resultCode !== RESULT_CODE.SUCCESS) {
      showNotification({
        title: t("ERROR"),
        message: otpRes.body?.resultDesc || t("RESEND_OTP_FAILED"),
        color: "red",
      });
      throw new Error("resend otp failed");
    }
    setLinkOtpId(otpRes.body?.otpId || "");
    showNotification({
      title: t("NOTICE"),
      message: t("OTP_RESENT"),
      color: "green",
    });
  };

  /** Submit OTP → gọi linkToBiz confirm → success thì navigate sang result page. */
  const handleConfirmLinkOtp = async () => {
    if (!linkRefId || Number.isNaN(numericCardId)) {
      showNotification({
        title: t("ERROR"),
        message: t("MISSING_REQUEST_INFO"),
        color: "red",
      });
      return;
    }
    if (!linkOtpForm.values.otp || linkOtpForm.values.otp.length < 6) {
      showNotification({
        title: t("ERROR"),
        message: t("ENTER_ALL_6_OTP_CHARACTERS"),
        color: "red",
      });
      return;
    }
    const authType = Number(linkAuthObjects[0]?.authType) || 1;
    try {
      const authObject = {
        authType,
        otpId: linkOtpId,
        otp: SHA256(linkOtpForm.values.otp).toString(),
      };
      const res = await (isCredit
        ? linkToBizCredit({
            refId: linkRefId,
            cardId: numericCardId,
            remark:
              t("LINK_CARD_WITH_JAVIS_BIZ") || t("LINK_CARD_WITH_JAVIS_BIZ"),
            authObject,
          })
        : linkToBiz({
            refId: linkRefId,
            cardId: numericCardId,
            authObject,
          })
      ).unwrap();
      if (res.body?.resultCode !== RESULT_CODE.SUCCESS) {
        showNotification({
          title: t("ERROR"),
          message: res.body?.resultDesc || t("LINK_CONFIRMATION_FAILED"),
          color: "red",
        });
        linkOtpForm.setFieldValue("otp", "");
        return;
      }
      closeLinkOtp();
      setLinked(pendingLinkValue);
      navigate(RoutesString.CardActionResult, {
        state: {
          actionType: "LINK_TO_BIZ",
          detailObjectList: res.body?.detailObjectList || [],
        },
      });
    } catch (err) {
      showNotification({
        title: t("ERROR"),
        message: t("CONNECTION_ERROR"),
        color: "red",
      });
      linkOtpForm.setFieldValue("otp", "");
    }
  };

  const handleCopyCardNumber = () => {
    navigator.clipboard
      .writeText(fullCardNumber)
      .then(() => {
        showNotification({
          title: t("COPIED"),
          message: t("CARD_NUMBER_COPIED"),
          color: "green",
        });
      })
      .catch((err) => console.error("Copy failed", err));
  };

  const cardStatusText =
    CARD_STATUS_KEY[String(cardDetail?.status ?? "")] !== undefined
      ? t(CARD_STATUS_KEY[String(cardDetail?.status ?? "")])
      : beText(cardDetail?.statusLabel) || t("ACTIVE");

  return (
    <Box>
      <Group position="apart" align="center" mb={16}>
        <Title fw={800} fz={24}>
          {t("CARD_INFORMATION")}
        </Title>
        {!!cardDetail && !hasDetailError && (canViewSensitive || isCredit) && (
          <Button
            variant="light"
            color="blue"
            radius={8}
            leftIcon={<IconEye size={16} />}
            onClick={handleViewCardDetail}
            loading={isCheckingScope || isGeneratingOtp}
            styles={{
              root: {
                background: "#E5F4FE",
                color: "#2A81D0",
                fontWeight: 600,
              },
            }}
          >
            {t("VIEW_CARD_DETAIL")}
          </Button>
        )}
      </Group>

      <Box style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {/* LEFT — Card info */}
        <Box style={{ flex: 1 }}>
          <Box
            style={{
              background: "#FDFDFD",
              borderRadius: 12,
              boxShadow: "0px 2px 8px 0px #638DBF1A",
              overflow: "hidden",
            }}
          >
            {/* Card image */}
            <Box
              style={{
                background: "#F5F7FA",
                margin: "20px 20px 0",
                borderRadius: 12,
                padding: "32px 20px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                src={cardImage}
                alt="card"
                style={{
                  width: 380,
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Info grid */}
            <Box p="20px">
              <Grid gutter={24} align="flex-start">
                {/* Hàng 1: Tên chủ thẻ | Số tài khoản | Hạn mức khả dụng | Tên sản phẩm */}
                <Grid.Col span={3}>
                  <Text size="xs" color="#5E656B" tt="uppercase" mb={4}>
                    {t("CARD_HOLDERS_NAME")}
                  </Text>
                  <Text fw={700} color="#131618" tt="uppercase">
                    {cardInfo.holderName}
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text size="xs" color="#5E656B" tt="uppercase" mb={4}>
                    {t("ACCOUNT_NUMBER")}
                  </Text>
                  <Text fw={700} color="#131618">
                    {cardInfo.accountNumber}
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text size="xs" color="#5E656B" tt="uppercase" mb={4}>
                    {t("AVAILABLE_LIMIT")}
                  </Text>
                  <Text fw={700} color="#2A81D0">
                    {cardInfo.availableLimit}
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text size="xs" color="#5E656B" tt="uppercase" mb={4}>
                    {t("PRODUCT_NAME")}
                  </Text>
                  <Text fw={700} color="#131618">
                    {cardInfo.productName}
                  </Text>
                </Grid.Col>
                {/* Divider mảnh, màu nhạt sát nền phân tách 2 hàng thông tin */}
                <Grid.Col span={12}>
                  <Box
                    style={{
                      height: 1,
                      background: "#EFF1F4",
                      margin: "4px 0",
                    }}
                  />
                </Grid.Col>
                {/* Hàng 2: Công ty | Liên kết Javis Biz (label + switch) | Trạng thái */}
                <Grid.Col span={3}>
                  <Text size="xs" color="#5E656B" tt="uppercase" mb={4}>
                    {t("COMPANY")}
                  </Text>
                  <Text fw={700} color="#131618">
                    {cardInfo.cardBusiness}
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Group
                    spacing={12}
                    noWrap
                    position="apart"
                    align="center"
                    sx={{ minHeight: 40 }}
                  >
                    <Text size="sm" color="#131618" fw={500}>
                      {t("LINK_CARD_WITH_JAVIS_BIZ")}
                    </Text>
                    <Switch
                      checked={linked}
                      onChange={handleToggleLink}
                      disabled={isInitLinking}
                      color="blue"
                      size="md"
                    />
                  </Group>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text size="xs" color="#5E656B" tt="uppercase" mb={4}>
                    {t("STATUS")}
                  </Text>
                  <Badge
                    radius="xl"
                    variant="outline"
                    sx={{
                      color: isLocked ? "#DC2626" : "#34AA44",
                      borderColor: isLocked ? "#DC2626" : "#34AA44",
                      background: "transparent",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    {/* #56083: ưu tiên MÃ trạng thái -> nhãn i18n (như màn Card
                        list), chỉ dùng statusLabel (BE tiếng Việt) làm dự phòng và
                        dịch qua beText; trước đây statusLabel đè nên EN vẫn ra VN. */}
                    {cardStatusText}
                  </Badge>
                </Grid.Col>
                <Grid.Col span={3} />
                {/* Hàng 3: Địa chỉ full-width */}
                <Grid.Col span={12}>
                  <Text size="xs" color="#5E656B" tt="uppercase" mb={4}>
                    {t("ADDRESS")}
                  </Text>
                  <Text fw={700} color="#131618">
                    {cardInfo.address}
                  </Text>
                </Grid.Col>
              </Grid>
            </Box>
          </Box>
        </Box>

        {/* RIGHT — Action sidebar (compact) */}
        <Box
          sx={{
            width: 280,
            alignSelf: "flex-start",
            background: "#F3F5F7",
            borderRadius: 12,
            padding: 8,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {actions.map(({ icon: Icon, img, label, type, color, bg }) => {
            const enabled = isActionEnabled(type);
            return (
              <Box
                key={type}
                onClick={enabled ? () => handleOperation(type) : undefined}
                sx={{
                  background: "#FFFFFF",
                  borderRadius: 8,
                  padding: "8px 10px",
                  minHeight: 44,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: enabled ? "pointer" : "not-allowed",
                  opacity: enabled ? 1 : 0.5,
                  transition: "background 0.15s",
                  "&:hover": { background: enabled ? "#F7F9FB" : "#FFFFFF" },
                }}
              >
                {/* Credit: SVG thiết kế (đã gồm ô + màu). Debit: Tabler icon trong ô bo tròn. */}
                {img ? (
                  <img
                    src={img}
                    alt=""
                    width={32}
                    height={32}
                    style={{ flexShrink: 0, display: "block" }}
                  />
                ) : (
                  Icon && (
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: bg || "#EEF5FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon size={16} color={color || "#2A81D0"} stroke={2} />
                    </Box>
                  )
                )}
                <Text
                  sx={{
                    flex: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#111827",
                    lineHeight: 1.3,
                  }}
                >
                  {label}
                </Text>
                <IconChevronRight
                  size={14}
                  color="#9CA3AF"
                  style={{ flexShrink: 0 }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Modal: Xem chi tiết thẻ */}
      <Modal
        opened={openedModal}
        onClose={closeModal}
        title={t("CARD_INFORMANTION")}
        centered
        overlayProps={{ opacity: 0.1, blur: 0.0 }}
        styles={{
          header: { borderBottom: "solid 1px #dee2e6" },
          title: {
            fontWeight: 700,
            fontSize: 18,
            color: "#131618",
          },
        }}
        sx={{ width: 446 }}
      >
        <Box>
          <Box
            sx={{
              padding: 20,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img src={cardModalImage} alt="" width={284} />
          </Box>
          <Grid gutter="xs">
            <Grid.Col span={6} sx={{ color: "#5E656B" }}>
              {t("CARD_NUMBER")}
            </Grid.Col>
            <Grid.Col
              span={6}
              style={{
                textAlign: "right",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {sensitiveInfo?.cardNumber || fullCardNumber}
                <IconCopy
                  onClick={handleCopyCardNumber}
                  color="#989FA6"
                  style={{ cursor: "pointer" }}
                />
              </span>
            </Grid.Col>
          </Grid>
          <Grid gutter="xs">
            <Grid.Col span={6} sx={{ color: "#5E656B" }}>
              {t("EXPIRY_DATE")}
            </Grid.Col>
            <Grid.Col span={6} style={{ textAlign: "right" }}>
              {sensitiveInfo?.expiryDate || cardDetail?.expiryDate || "-"}
            </Grid.Col>
          </Grid>
          <Grid gutter="xs">
            <Grid.Col span={6} sx={{ color: "#5E656B" }}>
              CVV
            </Grid.Col>
            <Grid.Col
              span={6}
              style={{
                textAlign: "right",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {(() => {
                  if (!sensitiveInfo?.cvv) return "-";
                  return showCvv ? sensitiveInfo.cvv : "***";
                })()}
                {sensitiveInfo?.cvv &&
                  (showCvv ? (
                    <IconEyeOff
                      onClick={() => setShowCvv(false)}
                      color="#989FA6"
                      style={{ cursor: "pointer" }}
                      size={20}
                    />
                  ) : (
                    <IconEye
                      onClick={() => setShowCvv(true)}
                      color="#989FA6"
                      style={{ cursor: "pointer" }}
                      size={20}
                    />
                  ))}
              </span>
            </Grid.Col>
          </Grid>
          <Box
            sx={{
              width: "100%",
              background: "#E5F4FE",
              color: "#5E656B",
              textAlign: "center",
              padding: 8,
              borderRadius: 8,
              marginTop: 10,
            }}
          >
            {t("THE_POPUP_WILL_BE_CLOSED_AFTER")}
            <span className="text-[#2A81D0]"> {countdown} </span>
            {t("SECOND")}
          </Box>
        </Box>
      </Modal>

      <CardDetailActionDrawer
        opened={openedAction}
        close={closeAction}
        label={labelAction}
        cardId={Number.isNaN(numericCardId) ? undefined : numericCardId}
        cardDetail={cardDetail}
      />
      <AuthenticationMode
        openedDrawer={openedDrawer}
        closeDrawer={closeDrawer}
        openModal={openModal}
        openedModal={openedModal}
        cardId={Number.isNaN(numericCardId) ? undefined : numericCardId}
        refId={viewRefId}
        otpId={viewOtpId}
        isCredit={isCredit}
        onSuccess={handleSensitiveInfoSuccess}
      />

      {/* Toggle link warning modal */}
      <Modal
        opened={openedToggleWarn}
        onClose={closeToggleWarn}
        centered
        withCloseButton={false}
        size={420}
        radius={16}
        padding={32}
        overlayProps={{ opacity: 0.4, blur: 2 }}
        styles={{ body: { padding: 0 } }}
      >
        <Stack align="center" spacing={20} p={32}>
          {/* Error icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "#DC2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconX size={32} color="#FFFFFF" stroke={3} />
            </Box>
          </Box>

          <Text fw={700} fz={18} color="#131618" ta="center">
            {t("NOTIFICATION")}
          </Text>

          <Text size="sm" color="#5E656B" ta="center">
            {t("CANNOT_TOGGLE_INACTIVE_CARD")}
          </Text>

          <Stack spacing={12} sx={{ width: "100%" }}>
            <Button
              fullWidth
              radius={8}
              onClick={closeToggleWarn}
              styles={{
                root: {
                  background: "#2A81D0",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  height: 44,
                  "&:hover": { background: "#1F6AB5" },
                },
              }}
            >
              {t("CLOSE")}
            </Button>
            <Button
              fullWidth
              variant="outline"
              radius={8}
              onClick={() => {
                closeToggleWarn();
                navigate(RoutesString.RequirementsManagementPage);
              }}
              styles={{
                root: {
                  borderColor: "#2A81D0",
                  color: "#2A81D0",
                  fontWeight: 600,
                  height: 44,
                },
              }}
            >
              {t("MANAGE_REQUEST")}
            </Button>
          </Stack>
        </Stack>
      </Modal>

      {/* JVC-3542: popup thẻ chưa liên kết Javis Biz — icon lỗi đỏ + nút Đóng theo thiết kế */}
      <Modal
        opened={openedNotLinkedWarn}
        onClose={closeNotLinkedWarn}
        centered
        withCloseButton={false}
        size={420}
        radius={16}
        padding={32}
        overlayProps={{ opacity: 0.4, blur: 2 }}
        styles={{ body: { padding: 0 } }}
      >
        <Stack align="center" spacing={20} p={32}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "#DC2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconX size={32} color="#FFFFFF" stroke={3} />
            </Box>
          </Box>
          <Text fw={700} fz={18} color="#131618" ta="center">
            {t("NOTIFICATION")}
          </Text>
          <Text size="sm" color="#5E656B" ta="center">
            {t("CARD_NOT_LINKED_NOTE")}
          </Text>
          <Button
            fullWidth
            radius={8}
            onClick={closeNotLinkedWarn}
            styles={{
              root: {
                background: "#2A81D0",
                color: "#FFFFFF",
                fontWeight: 600,
                height: 44,
                "&:hover": { background: "#1F6AB5" },
              },
            }}
          >
            {t("CLOSE")}
          </Button>
        </Stack>
      </Modal>

      {/* Popup lỗi quyền thao tác — fallback khi deep-link thẳng vào chi tiết */}
      <CardPermissionErrorModal
        opened={openedDetailError}
        onClose={handleCloseDetailError}
        message={detailErrorMessage}
        onManageRequest={handleGoManageRequest}
      />

      {/* Confirm link toggle modal — icon "?" cam, 2 nút Đồng ý / Đóng */}
      <Modal
        opened={openedConfirmLink}
        onClose={closeConfirmLink}
        centered
        withCloseButton={false}
        size={400}
        radius={16}
        padding={32}
        overlayProps={{ opacity: 0.4, blur: 2 }}
        styles={{ body: { padding: 0 } }}
      >
        <Stack align="center" spacing={16} p={32}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "#E5F4FE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconMessage2Question size={36} color="#2A81D0" stroke={1.8} />
          </Box>

          <Text fw={700} fz={18} color="#131618" ta="center">
            {linked ? t("CONFIRM_CARD_UNLINK") : t("CONFIRM_CARD_LINK")}
          </Text>

          <Text size="sm" color="#5E656B" ta="center">
            {linked
              ? t("CONFIRM_UNLINK_CARD")
              : t("CONFIRM_LINK_CARD")}
          </Text>

          <Stack spacing={12} sx={{ width: "100%" }}>
            <Button
              fullWidth
              radius={8}
              loading={isInitLinking || isGeneratingOtp}
              onClick={handleConfirmLink}
              styles={{
                root: {
                  background: "#2A81D0",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  height: 44,
                  "&:hover": { background: "#1F6AB5" },
                },
              }}
            >
              {t("AGREE")}
            </Button>
            <Button
              fullWidth
              variant="outline"
              radius={8}
              onClick={closeConfirmLink}
              styles={{
                root: {
                  borderColor: "#2A81D0",
                  color: "#2A81D0",
                  fontWeight: 600,
                  height: 44,
                },
              }}
            >
              {t("CLOSE")}
            </Button>
          </Stack>
        </Stack>
      </Modal>

      <OtpModal
        opened={openedLinkOtp}
        onClose={closeLinkOtp}
        onConfirm={handleConfirmLinkOtp}
        otp={linkOtpForm.values.otp}
        onChangeOtp={(v) => linkOtpForm.setFieldValue("otp", v)}
        expireTime={120}
        isLoading={isConfirmingLink}
        handleSendOtp={handleResendLinkOtp}
      />
    </Box>
  );
};

export default CardDetailWrapper;
